import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Chip, ChipSize } from '@amzn/stencil-react-components/chip';

/**
 * Hoisted so the object identity is stable across renders.
 *
 * The tracking is a raw value because Stencil ships no letter-spacing token. It is in
 * `em` so it scales with the label's font size rather than fixing a pixel width.
 */
const SECTION_LABEL = { textTransform: 'uppercase', letterSpacing: '0.05em' } as const;

/**
 * Where the badge sits. `inline` reads as part of the heading, which suits a figure
 * drawn from it. `end` sits at the far edge, which suits a scope note that qualifies the
 * whole section rather than the label.
 */
export type BadgeAlign = 'inline' | 'end';

interface SectionHeadingProps {
  icon: JSX.Element;
  label: string;
  /** A figure that qualifies the heading, such as how many questions passed. */
  badge?: string;
  /** Defaults to `inline`. */
  badgeAlign?: BadgeAlign;
}

/** Heading shared by the insight sections: icon, upper-cased label, optional qualifier. */
export function SectionHeading({
  icon,
  label,
  badge,
  badgeAlign = 'inline',
}: SectionHeadingProps): JSX.Element {
  const chip = badge ? <Chip size={ChipSize.ExtraSmall}>{badge}</Chip> : null;
  return (
    <Row alignItems="center" gridGap="dimensions.spacing.200" flexWrap="wrap">
      {icon}
      {/* Sized to match the item titles beneath it, not below them: a heading smaller
          than its own contents reads as a caption on them rather than a heading over
          them. Upper case, tracking and the secondary colour carry the distinction, as
          they do in the prototype, where both are the same size. */}
      <Text
        fontSize="T200"
        fontWeight="bold"
        color="color.text.secondary"
        css={SECTION_LABEL}
      >
        {label}
      </Text>
      {badgeAlign === 'inline' ? chip : null}
      {badgeAlign === 'end' && chip ? (
        <Row justifyContent="flex-end" flex={1}>
          {chip}
        </Row>
      ) : null}
    </Row>
  );
}
